import React, { useEffect, useRef, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import {
  IconButton,
  InputAdornment,
  InputBaseComponentProps,
  TextField
} from '@mui/material'
import styled, { css } from 'styled-components'

import {
  getCharacterCounterColor,
  getFieldCharacterLimit} from 'components/atoms/AiAssistant/utils/ai-assistant.utils'
import type { CharacterCounterVariant } from 'components/atoms/CharacterCounter'
import { CharacterCounter } from 'components/atoms/CharacterCounter'
import { RequiredStar } from 'components/atoms/RequiredStar'
import { noop } from 'utils/shared'
import theme from 'utils/theme'

/**
 * Wraps the actual input so we can defer react-hook-form updates (startTransition).
 * Input value updates immediately from local state; form state updates in background.
 * Reduces input lag when the form has many fields or heavy re-renders.
 */
function DeferredFieldInput({
  field,
  id,
  label,
  required,
  multiline,
  rows,
  type,
  inputType,
  placeholder,
  disabled,
  inputProps,
  starticon,
  endAdornment,
  error,
  helperText,
  variant,
  InputLabelProps
}: {
  field: { value: string; onChange: (e: any) => void; onBlur: () => void; name: string; ref: (r: any) => void }
  id?: string
  label?: string
  required?: boolean
  multiline?: boolean
  rows?: number
  type?: string
  inputType?: string
  placeholder?: string
  disabled?: boolean
  inputProps?: any
  starticon?: React.ReactNode
  endAdornment?: React.ReactNode
  error?: boolean
  helperText?: string
  variant?: 'standard' | 'outlined' | 'filled'
  InputLabelProps?: any
}) {
  const [localValue, setLocalValue] = useState(() => field.value ?? '')
  const lastSentRef = useRef<string>(String(field.value ?? ''))
  // useTransition marks form update as non-urgent so input stays responsive (React 18)
  const [, startTransition] = (React as any).useTransition?.() ?? [false, (fn: () => void) => fn()]

  // Sync from form when value is set externally (reset, setValue from parent)
  useEffect(() => {
    const formVal = String(field.value ?? '')

    if (formVal !== lastSentRef.current) {
      lastSentRef.current = formVal
      setLocalValue(formVal)
    }
  }, [field.value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value

    setLocalValue(val)
    startTransition(() => {
      lastSentRef.current = val
      field.onChange(e)
    })
  }

  const inputId = id ?? field.name

  return (
    <TextField
      name={field.name}
      value={localValue}
      onChange={handleChange}
      onBlur={field.onBlur}
      inputRef={field.ref}
      id={inputId}
      label={required ? <>{label}<RequiredStar /></> : label}
      multiline={multiline}
      rows={rows}
      type={inputType || type || 'text'}
      placeholder={placeholder}
      disabled={disabled}
      inputProps={{
        ...inputProps,
        id: inputId,
        name: field.name
      }}
      InputProps={{
        startAdornment: starticon ?? null,
        endAdornment: endAdornment ?? null
      }}
      error={error}
      helperText={helperText}
      variant={variant}
      InputLabelProps={{
        ...InputLabelProps,
        htmlFor: inputId
      }}
    />
  )
}

type TSize =
  | 'fit-content'
  | 'medium'
  | 'halfProductForm'
  | 'large'
  | 'fullProductForm'
  | 'extraLarge'

interface InputFProps {
  id?: string
  onClick?: () => void
  name: string
  type?: string
  inputProps?: InputBaseComponentProps | undefined
  label?: string
  placeholder?: string
  disabled?: boolean
  starticon?: React.ReactElement | string | null
  endicon?: React.ReactElement | string | null
  required?: boolean
  size?: TSize
  variant?: 'standard' | 'outlined' | 'filled'
  multiline?: boolean
  rows?: number
  rules?: any
  InputLabelProps?: any
  showCharacterCounter?: boolean
  maxCharacterCount?: number
  assetType?: string // For dynamic character limits based on asset type
  characterCounterVariant?: CharacterCounterVariant
}

export const InputF = (props: InputFProps): JSX.Element => {
  const {
    id,
    onClick,
    name,
    inputProps = {},
    type = 'text',
    label,
    placeholder,
    disabled = false,
    starticon,
    endicon,
    required,
    size = 'large',
    rules,
    variant = 'standard',
    multiline = false,
    rows = 1,
    InputLabelProps,
    showCharacterCounter = false,
    maxCharacterCount = 125,
    characterCounterVariant = 'default'
  } = props
  const [passwordVisible, setPasswordVisibility] = useState(false)
  const { control } = useFormContext()

  const inputType = !type || passwordVisible ? 'text' : type

  const handleVisibilityChange = () =>
    setPasswordVisibility(prevVisibility => !prevVisibility)

  const getEndAdornment = type === 'password' && (
    <InputAdornment position='end'>
      <IconButton
        style={{ color: theme.colors.icon }}
        onClick={handleVisibilityChange}
      >
        {passwordVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}
      </IconButton>
    </InputAdornment>
  )

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        const currentLength = (typeof field.value === 'string' ? field.value : String(field.value ?? ''))?.length || 0

        const fieldNameParts = name.split('.')
        const actualFieldName = fieldNameParts[fieldNameParts.length - 1]
        const utilityLimit = getFieldCharacterLimit(
          actualFieldName,
          props.assetType
        )
        const knownFields = new Set([
          'name',
          'packshotAlt',
          'openedPackshotAlt',
          'hotpointsAlt',
          'hotpointsMobileAlt',
          'dimensionsAlt',
          'summaryAlt',
          'attributeAlt',
          'attributeAltMobile',
          'defaultDescription',
          'descriptionForEuro',
          'descriptionForTIM',
          'disclaimer',
          'dimensionsDescription',
          'summaryDescription',
          'additionalText',
          'description'
        ])
        const isKnownField = knownFields.has(actualFieldName)
        const unlimitedFields = new Set([
          'sku',
          'skuCodes',
          'shortName',
          'internalNote',
          /** Comparison custom product CTA URLs (can be long query strings) */
          'link',
          /** LP / asset video URLs (S3 paths exceed default 125-char input cap) */
          'videoUrl',
          'videoYoutubeId'
        ])
        const isUnlimitedField = unlimitedFields.has(actualFieldName)
        const hasExplicitMaxCount = props.maxCharacterCount !== undefined
        const characterLimit = isUnlimitedField
          ? undefined
          : hasExplicitMaxCount
          ? maxCharacterCount
          : isKnownField
          ? utilityLimit
          : maxCharacterCount
        const counterColor = isKnownField
          ? getCharacterCounterColor(
              actualFieldName,
              currentLength,
              props.assetType
            )
          : undefined

        return (
          <InputWrapper
            onClick={onClick || noop}
            size={size}
            $multiline={multiline}
          >
            <DeferredFieldInput
              field={field}
              id={id}
              label={label}
              required={required}
              multiline={multiline}
              rows={rows}
              type={type}
              inputType={inputType}
              placeholder={placeholder}
              disabled={disabled}
              inputProps={{
                ...inputProps,
                className: 'textArea',
                maxLength: characterLimit
              }}
              starticon={starticon ? <InputAdornment position='start'>{starticon}</InputAdornment> : null}
              endAdornment={endicon || getEndAdornment}
              error={!!error}
              helperText={
                typeof error?.message === 'string' ? error.message : undefined
              }
              variant={variant}
              InputLabelProps={{ ...InputLabelProps }}
            />
            {showCharacterCounter && (
              <CharacterCounter
                currentLength={currentLength}
                maxLength={characterLimit}
                color={counterColor}
                variant={characterCounterVariant}
              />
            )}
          </InputWrapper>
        )
      }}
    />
  )
}

const InputWrapper = styled.div<{
  size?: TSize
  $multiline: boolean
}>`
  position: relative;
  width: ${({ size }) => (size === 'fit-content' ? '100%' : undefined)};

  .MuiFormControl-root {
    width: ${({ size }) => (size === 'medium' ? '208px' : '816px')} !important;
    height: ${({ size, $multiline }) =>
      $multiline ? 'auto' : size === 'medium' ? '40px' : '48px'} !important;

    ${({ size, $multiline }) =>
      size === 'fit-content' &&
      css`
        width: 100% !important;
        height: auto !important;
        min-height: ${$multiline ? 'auto' : '48px'};
      `}

    ${({ size }) =>
      size === 'medium' &&
      css`
        width: 208px !important;
        height: 40px !important;
      `}
    ${({ size }) =>
      size === 'halfProductForm' &&
      css`
        width: 496px !important;
        height: 48px !important;
      `}
    ${({ size }) =>
      size === 'large' &&
      css`
        width: 516px !important;
        height: 48px !important;
      `}
    ${({ size }) =>
      size === 'fullProductForm' &&
      css`
        width: 1060px !important;
        height: 48px !important;
      `}
    ${({ size }) =>
      size === 'extraLarge' &&
      css`
        width: 982px !important;
        height: 48px !important;
      `}
    ${({ $multiline }) =>
      $multiline &&
      css`
        height: auto !important;
      `}
  }

  .MuiFormHelperText-root {
    bottom: -22px !important;
    left: 5px !important;

    &.Mui-error {
      color: ${({ theme }) => theme.colors.error};
    }
  }

  .Mui-error {
    color: ${({ theme }) => theme.colors.error} !important;
  }

  .textArea {
    resize: both;
    overflow: hidden;
  }
`
